/**
 * Convierte filtros de UI al payload del motor.
 */

/** Lista, rango o texto único listo para el operador del motor. */
export function normalizeFilterValues(val) {
    if (val == null || val === '') return [];
    if (Array.isArray(val)) return val.filter(item => item !== '' && item != null).map(String);
    if (typeof val === 'object' && (val.from || val.to)) return val;
    return [String(val)];
}

/**
 * ¿La fuente `dataSourceId` incluye a `targetId`? Cierto si son la misma, o si
 * `dataSourceId` es una unión (kind: 'join') que combina, directa o indirectamente,
 * a `targetId` con otra fuente.
 */
export function sourceIncludesDataSource(dataSourceId, targetId, catalog = {}) {
    if (!dataSourceId || !targetId) return false;
    if (dataSourceId === targetId) return true;
    const dataSources = catalog.dataSources || catalog.data_sources || [];
    const joins = catalog.joins || [];
    const source = dataSources.find(item => item.id === dataSourceId);
    if (!source || source.kind !== 'join') return false;
    const join = joins.find(item => item.id === source.joinId);
    if (!join) return false;
    return sourceIncludesDataSource(join.leftDataSourceId, targetId, catalog)
        || sourceIncludesDataSource(join.rightDataSourceId, targetId, catalog);
}

/** Filtros de la barra global del dashboard, acotados a una fuente (incluye uniones que la combinan). */
export function buildEngineFilters(filterDefs = [], filterValues = {}, dataSourceId, catalog) {
    return (filterDefs || []).filter(def => {
        if (!dataSourceId || !def?.dataSourceId) return true;
        return sourceIncludesDataSource(dataSourceId, def.dataSourceId, catalog);
    }).map(def => {
        const val = filterValues[def.id];
        if (['select', 'dropdown', 'multiselect'].includes(def.type)) {
            const list = normalizeFilterValues(val);
            if (!list.length) return null;
            if (list.length === 1) return { column: def.column, operator: 'eq', value: list[0] };
            return { column: def.column, operator: 'in', value: list };
        }
        if (def.type === 'dateRange' && val && (val.from || val.to)) {
            return { column: def.column, operator: 'between', value: [val.from || '', val.to || ''] };
        }
        if (def.type === 'text' && val) {
            return { column: def.column, operator: 'contains', value: val };
        }
        return null;
    }).filter(Boolean);
}

/** Tipo de control sugerido según el esquema de la columna. */
export function defaultFilterType(inferredType, uniqueCount) {
    if (inferredType === 'date') return 'dateRange';
    if (inferredType === 'string' || (inferredType === 'number' && uniqueCount > 0 && uniqueCount <= 400)) {
        return 'multiselect';
    }
    return 'text';
}

/** Alias de columna en el widget, o el nombre crudo. */
export function getAlias(query, name) {
    return query?.aliases?.[name] || name;
}

export const METRIC_FILTER_OPS = [
    { value: 'eq', label: '=' },
    { value: 'neq', label: '≠' },
    { value: 'gt', label: '>' },
    { value: 'gte', label: '≥' },
    { value: 'lt', label: '<' },
    { value: 'lte', label: '≤' },
    { value: 'between', label: 'Entre' },
    { value: 'empty', label: 'Vacío' },
    { value: 'not_empty', label: 'No vacío' }
];

export const TEXT_FILTER_OPS = [
    { value: 'eq', label: 'Igual a' },
    { value: 'neq', label: 'Distinto de' },
    { value: 'in', label: 'Es uno de' },
    { value: 'not_in', label: 'No es uno de' },
    { value: 'contains', label: 'Contiene' },
    { value: 'not_contains', label: 'No contiene' },
    { value: 'starts_with', label: 'Empieza con' },
    { value: 'ends_with', label: 'Termina con' },
    { value: 'empty', label: 'Vacío' },
    { value: 'not_empty', label: 'No vacío' }
];

export const DATE_FILTER_OPS = [
    { value: 'eq', label: 'Igual a' },
    { value: 'neq', label: 'Distinto de' },
    { value: 'gt', label: 'Después de' },
    { value: 'gte', label: 'Desde' },
    { value: 'lt', label: 'Antes de' },
    { value: 'lte', label: 'Hasta' },
    { value: 'between', label: 'Entre' },
    { value: 'empty', label: 'Vacío' },
    { value: 'not_empty', label: 'No vacío' }
];

export const NUMBER_FILTER_OPS = METRIC_FILTER_OPS;

export function opsForFixedFilter(item, colMeta) {
    if (item?.role === 'metric') return METRIC_FILTER_OPS;
    const inferred = colMeta?.inferred_type || item?.inferred_type || 'string';
    if (inferred === 'number') return NUMBER_FILTER_OPS;
    if (inferred === 'date') return DATE_FILTER_OPS;
    return TEXT_FILTER_OPS;
}

export function defaultFixedFilterOperator(field) {
    const inferred = field?.inferred_type;
    if (field?.role === 'metric' || inferred === 'number') return 'gte';
    if (inferred === 'date') return 'between';
    return 'eq';
}

export function normalizeSourceFilters(list = []) {
    return (Array.isArray(list) ? list : []).map((item, index) => ({
        id: item.id || `sf-${index}-${String(item.column || 'col')}`,
        column: item.column || '',
        operator: item.operator || 'eq',
        value: item.value ?? '',
        inferred_type: item.inferred_type || 'string',
        role: item.role || 'dimension',
    })).filter(item => item.column);
}

export function createSourceFilter(columnMeta = {}) {
    const inferred = columnMeta.inferred_type || 'string';
    return {
        id: `sf-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        column: columnMeta.name || '',
        inferred_type: inferred,
        role: inferred === 'number' ? 'metric' : 'dimension',
        operator: defaultFixedFilterOperator({ inferred_type: inferred }),
        value: '',
    };
}

/** Filtros fijos del widget (no los de la barra global). */
export function buildFixedEngineFilters(fixedFilters = []) {
    return (fixedFilters || []).map(item => {
        if (!item?.column) return null;
        const op = String(item.operator || (item.role === 'metric' ? 'gte' : 'eq')).toLowerCase();
        if (op === 'empty' || op === 'not_empty') {
            return { column: item.column, operator: op, value: true };
        }
        if (item.value === '' || item.value == null) return null;
        if (op === 'between') {
            const parts = Array.isArray(item.value)
                ? item.value
                : String(item.value).split(/[,;]/).map(part => part.trim());
            if (parts.length < 2 || (!parts[0] && !parts[1])) return null;
            return { column: item.column, operator: 'between', value: [parts[0] || '', parts[1] || ''] };
        }
        if (op === 'in' || op === 'not_in') {
            const list = Array.isArray(item.value)
                ? item.value.map(String).map(part => part.trim()).filter(Boolean)
                : String(item.value).split(',').map(part => part.trim()).filter(Boolean);
            if (!list.length) return null;
            if (list.length === 1 && op === 'in') return { column: item.column, operator: 'eq', value: list[0] };
            return { column: item.column, operator: op, value: list };
        }
        return { column: item.column, operator: op, value: item.value };
    }).filter(Boolean);
}
